import { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Trash2, Play, Square, Repeat } from 'lucide-react';
import * as Tone from 'tone';
import { useEffectClipDialogStore } from '../store/effectClipDialogStore';
import { useEditorStore } from '../store/editorStore';
import { useLoopStore } from '../store/loopStore';
import { EFFECT_CATALOG, type EffectType, createToneEffect, hasMixAPI } from '../engine/effectsManager';
import { getToneAudioEngine } from '../renderer/rendererBridge';
import { ReverbVisualizer } from './ReverbVisualizer';
import { BaseModal } from '@/components/modal/BaseModal';
import useMediaQuery, { MD_BREAKPOINT } from '@/hooks/useMediaQuery';

type ToneEffect = Tone.Reverb | Tone.Freeverb | Tone.FeedbackDelay | Tone.Chorus | Tone.Distortion |
  Tone.Compressor | Tone.EQ3 | Tone.Phaser | Tone.Tremolo | Tone.BitCrusher | Tone.AutoFilter | Tone.Gain;

type PreviewMode = 'both' | 'effect' | 'audio';

export function EffectClipDialog() {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery(MD_BREAKPOINT);
  const { open, mode, effectType, params, selectionStartMs, selectionEndMs, effectClipId, updateParams, close } =
    useEffectClipDialogStore();

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [previewMode, setPreviewMode] = useState<PreviewMode>('both');
  const [loopEnabled, setLoopEnabled] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  // Refs to avoid stale closures in callbacks
  const previewModeRef = useRef(previewMode);
  previewModeRef.current = previewMode;
  const loopEnabledRef = useRef(loopEnabled);
  loopEnabledRef.current = loopEnabled;
  const isPlayingRef = useRef(isPlaying);
  isPlayingRef.current = isPlaying;
  const catalog = EFFECT_CATALOG[effectType as EffectType];
  const label = catalog?.label ?? effectType;

  // Preview audio chain refs
  const previewEffectRef = useRef<ToneEffect | null>(null);
  const previewAnalyserRef = useRef<Tone.Analyser | null>(null);
  const previewSynthRef = useRef<Tone.NoiseSynth | null>(null);
  const previewPlayerRef = useRef<Tone.Player | null>(null);
  const previewInitedRef = useRef(false);
  const loopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Initialize preview chain when dialog opens
  useEffect(() => {
    if (!open) {
      previewInitedRef.current = false;
      return;
    }

    let disposed = false;

    async function initPreview() {
      const effect = await createToneEffect(effectType, params);
      if (disposed) { effect.dispose(); return; }

      const analyser = new Tone.Analyser('waveform', 256);
      const synth = new Tone.NoiseSynth({
        noise: { type: 'white' },
        envelope: { attack: 0.005, decay: 0.15, sustain: 0 },
      });

      synth.connect(effect);
      effect.connect(analyser);
      analyser.toDestination();

      previewEffectRef.current = effect;
      previewAnalyserRef.current = analyser;
      previewSynthRef.current = synth;
      previewInitedRef.current = true;
    }

    initPreview();

    return () => {
      disposed = true;
      stopPreview();
      previewSynthRef.current?.dispose();
      previewSynthRef.current = null;
      previewPlayerRef.current?.stop();
      previewPlayerRef.current?.dispose();
      previewPlayerRef.current = null;
      try { previewEffectRef.current?.disconnect(); } catch { /* ok */ }
      previewEffectRef.current?.dispose();
      previewEffectRef.current = null;
      previewAnalyserRef.current?.dispose();
      previewAnalyserRef.current = null;
      previewInitedRef.current = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, effectType]);

  // Sync effect params to preview effect when sliders change
  useEffect(() => {
    if (!previewInitedRef.current || !previewEffectRef.current) return;
    const effect = previewEffectRef.current;

    if ('roomSize' in effect) {
      try { (effect as unknown as { roomSize: { value: number } }).roomSize.value = params.roomSize ?? 0.7; } catch { /* ok */ }
    }
    if ('brightness' in effect) {
      try { (effect as unknown as { brightness: number }).brightness = params.brightness ?? 50; } catch { /* ok */ }
    }
    if ('modulation' in effect) {
      try { (effect as unknown as { modulation: number }).modulation = params.modulation ?? 2; } catch { /* ok */ }
    }
    if ('preDelay' in effect) {
      try { (effect as unknown as { preDelay: { value: number } }).preDelay.value = params.preDelay ?? 0.01; } catch { /* ok */ }
    }
    if ('loGain' in effect) {
      try { (effect as unknown as { loGain: { value: number } }).loGain.value = params.loGain ?? 0; } catch { /* ok */ }
    }
    if ('hiGain' in effect) {
      try { (effect as unknown as { hiGain: { value: number } }).hiGain.value = params.hiGain ?? 0; } catch { /* ok */ }
    }
    if (hasMixAPI(effect)) {
      effect.setMix(params.wet ?? 0.5);
    } else if ('wet' in effect) {
      try { (effect as unknown as { wet: { value: number } }).wet.value = params.wet ?? 0.5; } catch { /* ok */ }
    }
    if ('delayTime' in effect && params.delayTime !== undefined) {
      try { (effect as unknown as { delayTime: { value: number } }).delayTime.value = params.delayTime; } catch { /* ok */ }
    }
    if ('feedback' in effect && params.feedback !== undefined) {
      try { (effect as unknown as { feedback: { value: number } }).feedback.value = params.feedback; } catch { /* ok */ }
    }
  }, [params, effectType]);

  const stopPreview = useCallback(() => {
    if (loopTimerRef.current) {
      clearTimeout(loopTimerRef.current);
      loopTimerRef.current = null;
    }
    if (previewPlayerRef.current) {
      try { previewPlayerRef.current.stop(); } catch { /* ok */ }
      previewPlayerRef.current.disconnect();
      previewPlayerRef.current.dispose();
      previewPlayerRef.current = null;
    }
    setIsPlaying(false);
  }, []);

  const startPreview = useCallback(async () => {
    await Tone.start();
    if (!previewInitedRef.current) return;

    stopPreview();

    const currentMode = previewModeRef.current;
    const effect = previewEffectRef.current;
    const analyser = previewAnalyserRef.current;

    if (!effect || !analyser) return;

    const mixVal = currentMode === 'effect' ? 1 : (params.wet ?? 0.5);
    if (hasMixAPI(effect)) {
      effect.setMix(mixVal);
    } else if ('wet' in effect) {
      try { (effect as unknown as { wet: { value: number } }).wet.value = mixVal; } catch { /* ok */ }
    }

    if (currentMode === 'effect') {
      previewSynthRef.current?.triggerAttackRelease('16n');
      setIsPlaying(true);
      const tailMs = Math.min(3000, Math.max(500, (params.roomSize ?? 0.7) * 3000));
      loopTimerRef.current = setTimeout(() => {
        if (loopEnabledRef.current) {
          startPreview();
        } else {
          setIsPlaying(false);
        }
      }, tailMs);
      return;
    }

    const engine = getToneAudioEngine();
    const buffer = engine?.getBuffer();
    if (!buffer) {
      previewSynthRef.current?.triggerAttackRelease('16n');
      setIsPlaying(true);
      setTimeout(() => setIsPlaying(false), 500);
      return;
    }

    let selStart = selectionStartMs;
    let selEnd = selectionEndMs;
    if (mode === 'edit' && effectClipId) {
      const state = useEditorStore.getState();
      for (const track of state.project.tracks) {
        if (track.type !== 'effect' || !track.effectClips) continue;
        const clip = track.effectClips.find((ec) => ec.id === effectClipId);
        if (clip) {
          selStart = clip.startMs;
          selEnd = clip.startMs + clip.durationMs;
          break;
        }
      }
    }

    const state = useEditorStore.getState();
    let sourceOffset = selStart / 1000;
    let clipAssetId: string | undefined;
    for (const track of state.project.tracks) {
      if (track.type !== 'audio') continue;
      for (const clip of track.clips) {
        const clipEnd = clip.startMs + clip.durationMs;
        if (clip.startMs <= selStart && clipEnd >= selEnd) {
          sourceOffset = (clip.sourceOffsetMs + (selStart - clip.startMs)) / 1000;
          clipAssetId = clip.assetId;
          break;
        }
      }
    }

    const playBuffer = (clipAssetId ? engine?.getBuffer(clipAssetId) : null) ?? buffer;
    const durationSec = Math.max(0.1, (selEnd - selStart) / 1000);

    const player = new Tone.Player(playBuffer);
    previewPlayerRef.current = player;

    if (currentMode === 'both') {
      player.connect(effect);
    } else {
      player.connect(analyser);
    }

    player.start(Tone.now(), sourceOffset, durationSec);
    setIsPlaying(true);

    player.onstop = () => {
      if (loopEnabledRef.current && previewPlayerRef.current === player) {
        player.start(Tone.now(), sourceOffset, durationSec);
      } else {
        setIsPlaying(false);
        previewPlayerRef.current = null;
        try { player.disconnect(); } catch { /* ok */ }
        player.dispose();
      }
    };
  }, [params, selectionStartMs, selectionEndMs, mode, effectClipId, stopPreview]);

  const handlePreview = useCallback(async () => {
    if (isPlayingRef.current) {
      stopPreview();
    } else {
      startPreview();
    }
  }, [startPreview, stopPreview]);

  useEffect(() => {
    if (!isPlayingRef.current) return;
    startPreview();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewMode, loopEnabled]);

  useEffect(() => {
    if (!open) stopPreview();
  }, [open, stopPreview]);

  // Close on Escape (desktop only — BaseModal handles ESC on mobile)
  useEffect(() => {
    if (!open || isMobile) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') close();
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, close, isMobile]);

  // Close on click outside (desktop only — BaseModal handles backdrop on mobile)
  useEffect(() => {
    if (!open || isMobile) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) close();
    }
    const timer = setTimeout(() => window.addEventListener('mousedown', handleClick), 50);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('mousedown', handleClick);
    };
  }, [open, close, isMobile]);

  // In edit mode, live-update the store as user changes params
  useEffect(() => {
    if (!open || mode !== 'edit' || !effectClipId) return;
    useEditorStore.getState().updateEffectClipParams(effectClipId, params);
  }, [open, mode, effectClipId, params]);

  const doDelete = () => {
    if (mode === 'edit' && effectClipId) {
      useEditorStore.getState().removeEffectClip(effectClipId);
      useEditorStore.getState().selectClip(null);
    }
    setConfirmDelete(false);
    close();
  };

  const handleDeleteRequest = () => {
    if (mode !== 'edit' || !effectClipId) return;
    const state = useEditorStore.getState();
    for (const track of state.project.tracks) {
      if (track.type !== 'effect' || !track.effectClips) continue;
      const clip = track.effectClips.find((ec) => ec.id === effectClipId);
      if (clip && track.effectClips.length === 1) {
        setConfirmDelete(true);
        return;
      }
    }
    doDelete();
  };

  const handleApply = () => {
    stopPreview();
    if (mode === 'add') {
      const state = useEditorStore.getState();
      const audioTrack = state.project.tracks.find((tr) => tr.type === 'audio');
      if (!audioTrack) { close(); return; }

      const existingTrack = state.project.tracks.find(
        (tr) => tr.type === 'effect' && tr.parentTrackId === audioTrack.id &&
          tr.effectClips?.some((ec) => ec.effectType === effectType),
      );

      const clipData = {
        startMs: selectionStartMs,
        durationMs: selectionEndMs - selectionStartMs,
        effectType,
        params: { ...params },
        bypassed: false,
      };

      if (existingTrack) {
        state.addEffectClip(existingTrack.id, clipData);
      } else {
        state.addEffectTrackWithClip(audioTrack.id, effectType, clipData);
      }

      useLoopStore.getState().clearSelection();
    }
    close();
  };

  if (!open || !catalog) return null;

  if (confirmDelete) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div className="w-72 bg-[#1C1C1E] border border-[#3A3A3C] rounded-xl shadow-2xl shadow-black/60 overflow-hidden">
          <div className="px-4 py-4 text-center">
            <p className="text-sm text-[#E5E5EA] font-medium mb-1">
              {t('editor.deleteEffectTitle', 'Delete Effect?')}
            </p>
            <p className="text-xs text-[#8E8E93]">
              {t('editor.deleteEffectDesc', 'This will remove the effect and its track from the timeline.')}
            </p>
          </div>
          <div className="flex border-t border-[#3A3A3C]">
            <button
              onClick={() => setConfirmDelete(false)}
              className="flex-1 py-2.5 text-sm text-[#0A84FF] hover:bg-[#2A2A2E] transition-colors border-r border-[#3A3A3C]"
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              onClick={doDelete}
              className="flex-1 py-2.5 text-sm font-medium text-red-400 hover:bg-red-400/10 transition-colors"
            >
              {t('editor.delete', 'Delete')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isReverb = effectType === 'reverb';

  const previewModes: { key: PreviewMode; label: string }[] = [
    { key: 'effect', label: t('editor.previewEffect', 'Effect Only') },
    { key: 'audio', label: t('editor.previewAudio', 'Audio Only') },
    { key: 'both', label: t('editor.previewBoth', 'Both') },
  ];

  const dialogContent = (
    <>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#2A2A2E] shrink-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-[#A855F7]">FX</span>
          <span className="text-sm font-medium text-[#E5E5EA]">
            {mode === 'add' ? label : `${t('editor.editEffect', 'Edit')} ${label}`}
          </span>
        </div>
        <button onClick={close} className="p-1 hover:bg-[#2A2A2E] rounded">
          <X size={14} color="#8E8E93" />
        </button>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {/* Reverb Visualizer */}
        {isReverb && (
          <div className="px-4 pt-3">
            <ReverbVisualizer params={params} previewAnalyser={previewAnalyserRef.current} />
          </div>
        )}

        {/* Parameters */}
        <div className="px-4 py-3 space-y-3">
          {Object.entries(params).map(([key, value]) => {
            const meta = catalog.params?.[key];
            const min = meta?.min ?? 0;
            const max = meta?.max ?? 1;
            const step = meta?.step ?? 0.01;
            const paramLabel = meta?.label ?? key;
            const unit = meta?.unit ?? '';
            const decimals = step >= 1 ? 0 : step >= 0.1 ? 1 : step >= 0.01 ? 2 : 3;
            const pct = ((value - min) / (max - min)) * 100;
            return (
              <div key={key} className={isMobile ? 'space-y-1' : 'grid grid-cols-[80px_1fr_56px] items-center gap-3'}>
                <div className={isMobile ? 'flex items-center justify-between' : 'contents'}>
                  <span className="text-[13px] text-[#a0a0a0] truncate" title={paramLabel}>{paramLabel}</span>
                  {isMobile && (
                    <span className="text-[13px] text-white tabular-nums font-mono shrink-0">
                      {value.toFixed(decimals)}{unit ? ` ${unit}` : ''}
                    </span>
                  )}
                </div>
                <div className="relative flex items-center">
                  <input
                    type="range"
                    min={min}
                    max={max}
                    step={step}
                    value={value}
                    onChange={(e) => updateParams({ [key]: Number(e.target.value) })}
                    className="dv-slider w-full"
                    style={{
                      background: `linear-gradient(to right, #a370f7 0%, #a370f7 ${pct}%, #444448 ${pct}%, #444448 100%)`,
                      height: isMobile ? 36 : undefined,
                    }}
                  />
                </div>
                {!isMobile && (
                  <span className="text-[13px] text-white text-right tabular-nums font-mono shrink-0">
                    {value.toFixed(decimals)}{unit ? ` ${unit}` : ''}
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* Preview controls */}
        <div className="px-4 pb-2">
          <div className={`flex items-center gap-1.5 p-1 rounded-lg bg-[#111113] border border-[#2A2A2E] ${isMobile ? 'flex-wrap' : ''}`}>
            <button
              onClick={handlePreview}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-medium transition-all ${
                isPlaying
                  ? 'bg-[#a370f7] text-white shadow-[0_0_12px_rgba(163,112,247,0.4)]'
                  : 'text-[#a0a0a0] hover:bg-[#2A2A2E] hover:text-white'
              }`}
            >
              {isPlaying ? <Square size={10} fill="currentColor" /> : <Play size={10} fill="currentColor" />}
              {isPlaying ? t('editor.stop', 'Stop') : t('editor.preview', 'Preview')}
            </button>

            <div className="w-px h-4 bg-[#2A2A2E]" />

            {previewModes.map(({ key, label: modeLabel }) => (
              <button
                key={key}
                onClick={() => setPreviewMode(key)}
                className={`px-2 py-1.5 rounded-md text-[11px] transition-colors ${
                  previewMode === key
                    ? 'bg-[#2A2A2E] text-white font-medium'
                    : 'text-[#636366] hover:text-[#a0a0a0]'
                }`}
              >
                {modeLabel}
              </button>
            ))}

            <div className="w-px h-4 bg-[#2A2A2E]" />

            <button
              onClick={() => setLoopEnabled(!loopEnabled)}
              className={`flex items-center gap-1 px-2 py-1.5 rounded-md text-[11px] transition-colors ${
                loopEnabled
                  ? 'bg-[#a370f7]/20 text-[#a370f7] font-medium'
                  : 'text-[#636366] hover:text-[#a0a0a0]'
              }`}
              title={t('editor.loop', 'Loop')}
            >
              <Repeat size={10} />
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className={`flex items-center justify-between px-4 py-3 border-t border-[#2A2A2E] shrink-0 ${isMobile ? 'pb-safe' : ''}`}>
        {mode === 'edit' ? (
          <button
            onClick={handleDeleteRequest}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[13px] text-red-400 hover:bg-red-400/10 transition-colors"
          >
            <Trash2 size={13} />
            {t('editor.deleteClip', 'Delete clip')}
          </button>
        ) : (
          <div />
        )}
        <div className="flex items-center gap-2">
          <button
            onClick={close}
            className="px-4 py-2 rounded-lg text-[13px] text-[#a0a0a0] hover:bg-[#2A2A2E] transition-colors"
          >
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            onClick={handleApply}
            className="px-5 py-2 rounded-lg text-[13px] font-medium bg-[#a370f7] text-white hover:bg-[#9257e5] transition-colors"
          >
            {mode === 'add' ? t('editor.addEffect', 'Add Effect') : t('common.done', 'Done')}
          </button>
        </div>
      </div>
    </>
  );

  // Mobile: render inside BaseModal bottomsheet
  if (isMobile) {
    return (
      <BaseModal
        isOpen={open}
        onClose={close}
        mobileVariant="bottomsheet"
        ariaLabel={label}
      >
        <div className="flex flex-col max-h-[90dvh] bg-[#1a1a1e]">
          {/* Drag handle */}
          <div className="flex justify-center py-2 shrink-0">
            <div className="w-8 h-1 rounded-full bg-white/20" />
          </div>
          {dialogContent}
        </div>
      </BaseModal>
    );
  }

  // Desktop: original fixed centered dialog
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div
        ref={ref}
        className="w-[420px] bg-[#1a1a1e] border border-[#3A3A3C] rounded-xl shadow-2xl shadow-black/60 flex flex-col max-h-[90vh]"
      >
        {dialogContent}
      </div>
    </div>
  );
}
